import { define, ui, s, when, select, always } from '@owenscorning/pcb.alpha';
import _ from 'lodash';

// Components
import placeholder_url from '../../../../../../placeholder';
import CMSHero from '../../../../../OC/PageBuilder/Hero.jsx';
import AspectRatio from '../../../AspectRatio.jsx';

// Utilities
import { useAttributeSet } from '../../../../../../hooks/use_attribute_set';

const articles = [
  'Article::Fleetwood',
  'Article::Roofing',
  'Article::Newsroom',
  'Article::InsulationResidential',
  'Article::InsulationIndustrial',
  'Article::Lumber',
  'Article::Applications',
  'Article::Paroc',
];

export default define`Hero`('0.0.1')({
  gapless: true,
  gutterless: true,
  view: ({ value }) => {
    let props = _.cloneDeep(value);

    if (Board.build.type === 'Article::InsulationSolutionsSuite') {
      props = {
        ...(props || {}),
        copy: {
          ...(props?.copy || {}),
          prehead: '',
          mainHeading: Board.settings.details?.information?.title,
        },
        tags: [
          Board.settings.details?.taxonomies?.primary_solution_center ||
            _.first(Board.settings.details?.taxonomies?.solution_centers),
          Board.settings.details?.taxonomies?.primary_industry ||
            _.first(Board.settings.details?.taxonomies?.industries),
          Board.settings.details?.taxonomies?.category,
        ].filter(x => x),
        author: Board.settings.details?.information?.writer,
        // TODO: get rid of this.  We should not be UI.Date at the highest level, instead in here just pass published
        // we would want to UI.Date wrap at lowest level
        published: (
          <UI.Date>{Board.settings.details?.information?.published}</UI.Date>
        ),
        images: { bgDesktop: Board.settings.details?.image },
        preset: 'custom',
        type: 'tier_4',
      };
    } else if (articles.includes(Board.build.type)) {
      // Obtain the categories from the attributeSet in order to properly map the selected
      // value on either the first category or the primary category to the corresponding
      // translated label
      let categories = []
      const { query: { name, path } = {} } = value?.taxonomies?.[1] || { query: {} };
      const attributeSet = useAttributeSet(name, path, Board.build.language)

      if (!_.isEmpty(attributeSet)) {
        categories = attributeSet.categories?.[0]?.contents
      }
      const tagValues = Board.settings.details?.taxonomies?.primary_category ||
                          _.first(Board.settings.details?.taxonomies?.categories) ||
                          []

      props = {
        ...(props || {}),
        copy: {
          ...(props?.copy || {}),
          prehead: '',
          mainHeading: Board.settings.details?.information?.title,
        },
        tags: [
          categories?.filter(x => tagValues.includes(x.value)).map(x => x.label)
        ].filter(x => x),
        author: Board.settings.details?.information?.writer,
        // TODO: get rid of this.  We should not be UI.Date at the highest level, instead in here just pass published
        // we would want to UI.Date wrap at lowest level
        published: (
          <UI.Date>{Board.settings.details?.information?.published}</UI.Date>
        ),
        images: { bgDesktop: Board.settings.details?.image },
        preset: 'custom',
        type: 'tier_4',
      };
    }

    return <CMSHero {...props} />;
  },
  edit: {
    preset: ui`Choices`.of({
      custom: 'Custom',
      home_t1: 'Home | Tier 1',
      roofing_t1: 'Roofing | Tier 1',
      doors_t1: 'Doors | Tier 1',
      insulation_t1: 'Insulation | Tier 1',
      composites_t1: 'Composites | Tier 1',
      com_insulation_hero: 'Commercial Insulation',
      res_insulation_hero: 'Residential Insulation',
      roofing_components_hero: 'Roofing Components',
    })({
      label: 'Hero Preset',
      default: 'custom',
    }),
    type: ui`Choices`.of({
      tier_2: 'Tier 2',
      tier_3: 'Tier 3',
      tier_4: 'Tier 4',
      tier_5: 'Tier 5',
      tier_6: 'Tier 6',
    })({
      label: 'Hero Type',
      default: 'tier_3',
      visible: when`../preset`.is.equal
        .to('custom')
        .then(true)
        .otherwise(false),
    }),
    tier6: ui`Form`.of({
      height: ui`Choices`.of({
        tall: 'Tall (680px)',
        medium: 'Medium (464px)',
        short: 'Short (240px)',
      })({
        label: 'Tier 6 Hero Image Height',
        default: 'medium',
      }),
      image: ui`Image`({
        label: 'Tier 6 Hero Image',
        crop: true,
      }),
    })({
      label: 'Tier 6',
      visible: when`../type`.is.equal.to('tier_6'),
    }),
    imageSizeInfo: ui`ImageSizeInfo`({
      moduleName: 'Hero',
      visible: when`../preset`.isnt.equal.to('custom').and
        .when`../type`.isnt.equal.to('tier_3').and
        .when`../showHeroImage`.isnt.equal.to('small'),
    }),
    carousel: ui`Form`.of({
      enabled: ui`Switch`({
        default: false,
      }),
      [s._]: ui`Tip`.of("By toggling this on, the hero space will include multiple images with their own text content, and the user can click the arrows to view them all. Carousel style card sets have a maximum of 10 cards.")
    })({
      label: 'Carousel',
      visible: when`../type`.is.equal.to('tier_5').and.when`../preset`.is.equal.to('custom')
    }),
    background: ui`Form`.of({
      mediaType: ui`Choices`.of({
        image: 'Image',
        video: 'Video',
      })({
        label: 'Media Type',
        default: 'image',
      }),
      desktop: ui`Image`({
        crop: true,
        label: 'Desktop',
        visible: when`../mediaType`.is.equal.to('image'),
      }),
      tablet: ui`Image`({
        crop: true,
        label: 'Tablet',
        visible: when`../mediaType`.is.equal.to('image'),
      }),
      mobile: ui`Image`({
        crops: true,
        label: 'Mobile',
        visible: when`../../preset`.is.equal.to('custom').and
          .when`../../type`.is.equal.to('tier_5')
      }),
      mp4: ui`Text`({
        label: 'MP4',
        visible: when`../mediaType`.is.equal.to('video'),
      }),
      webm: ui`Text`({
        label: 'WEBM',
        visible: when`../mediaType`.is.equal.to('video'),
      }),
    })({
      label: 'Hero Background',
      visible: when`../type`.is.equal.to('tier_5').and
        .when`../carousel/enabled`.isnt.equal.to(true).or
        .when`../preset`.isnt.equal.to('custom'),
    }),
    showHeroImage: ui`Choices`.of({
      standard: 'Standard Image',
      small: 'Small Image',
      none: 'No Image',
    })({
      label: 'Hero Image Size',
      default: 'standard',
      visible: when`../preset`.is.equal.to('custom').and
        .when`../type`.is.equal.to('tier_3'),
    }),
    smallImageSizeInfo: ui`ImageSizeInfo`({
      moduleName: 'Small Hero',
      visible: when`../preset`.is.equal.to('custom').and
        .when`../type`.is.equal.to('tier_3').and
        .when`../showHeroImage`.is.equal.to('small')
        .then(true)
        .otherwise(false),
    }),
    // PB-727: 'contentOrder' data for the ContentAndImage component is in reference to the CONTENT
    // but the labels in the hero UI here are in the context of the IMAGE, so they're switched
    contentOrder: ui`Form`.of({
      desktop: ui`Choices`.of({
        first: 'Right', // content first: image right
        last: 'Left', // content last: image left
      })({
        label: 'Desktop',
        default: 'first',
      }),
      mobile: ui`Choices`.of({
        last: 'Before Content', // content last: image first
        first: 'After Content', // content first: image last
      })({
        label: 'Mobile',
        default: 'last',
      }),
    })({
      label: 'Hero Image Placement',
      visible: when`../preset`.is.equal.to('custom').and
        .when`../type`.is.equal.to('tier_3').and.when`../showHeroImage`.is.equal
        .to('small')
        .then(true)
        .otherwise(false),
    }),
    mobileAspectRatio: AspectRatio({
      label: 'T4 Hero Mobile Image Ratio',
      visible: when`../preset`.is.equal.to('custom').and
        .when`../type`.is.equal.to('tier_4'),
    }),
    images: [
      {
        bgDesktop: ui`Image`({
          crop: true,
          askAlt: false,
          label: 'Desktop Image',
          defaultImage: placeholder_url(1340, 600),
        }),
        bgTablet: ui`Image`({
          crop: true,
          askAlt: false,
          label: 'Tablet Image',
          default: placeholder_url(1340, 600),
          visible: when`../../type`.is.equal.to('tier_6').or
            .when`../../preset`.is.equal.to('custom').and
            .when`../../type`.is.equal.to('tier_3').and
            .when`../../showHeroImage`.is.equal.to('small')
            .then(false)
            .otherwise(true),
        }),
        bgMobile: ui`Image`({
          crop: true,
          askAlt: false,
          label: 'Mobile Image',
          defaultImage: placeholder_url(1340, 600),
          visible: when`../../preset`.is.equal.to('custom').and
            .when`../../type`.is.equal.to('tier_4'),
        }),
      },
      {
        label: 'Images',
        visible: when`../preset`.is.equal.to('custom').and
          .when`../type`.isnt.equal.to('tier_5').and
          .when`../type`.isnt.equal.to('tier_6'),
      },
    ],
    imgAlt: ui`Text`({
      label: 'Hero Alt Text',
      visible: when`../preset`.is.equal.to('custom').and
        .when`../type`.isnt.equal.to('tier_5').and
        .when`../type`.isnt.equal.to('tier_6')
    }),
    imageUrl: ui`Text`({
      label: 'Image Url (Optional)',
      visible: when`../preset`.is.equal.to('custom').and
        .when`../type`.is.equal.to('tier_3').and.when`../showHeroImage`.is.equal
        .to('small')
        .then(true)
        .otherwise(false),
    }),
    panther: ui`Form`.of({
      image: ui`Image`,
    })({
      label: 'Panther',
      visible: when`../preset`.isnt.equal.to('custom'),
    }),
    copy: ui`Form`.of({
      prehead: ui`Text`({
        
        label: 'Prehead', 
       
        default: 'Prehead',
        visible: when`../../type`.isnt.equal.to('tier_6').and
        .when`../../type`.isnt.equal.to('tier_5'),
      }),
      mainHeading: ui`Text/Rich/Header`({
        label: 'Main Heading',
        default: 'Heading',
      }),
      posthead: ui`Text`({
        label: 'Posthead',
        default: 'Posthead',
        visible: when`../../type`.is.equal.to('tier_3').or
          .when`../../type`.is.equal.to('tier_5'),
      }),
      body: ui`Text/Rich`({
        label: 'Body Copy',
        default: 'Lorum ipsum',
        visible: when`../../type`.isnt.equal.to('tier_5')
      }),
    })({
      label: 'Text Content',
      visible: when`../carousel/enabled`.isnt.equal.to(true),
    }),
    alertMessage: ui`Form`.of({
      display: ui`Switch`({
        default: false,
      }),
      type: ui`Choices`.of({
        black: 'Black',
        error: 'Error',
        info: 'Info',
        other: 'Other',
        success: 'Success',
        warning: 'Warning',
      })({
        label: 'Alert Type',
        default: 'black',
        visible: when`../display`.is.equal.to(true),
      }),
      title: ui`Text`({
        label: 'Title',
        visible: when`../display`.is.equal.to(true),
      }),
      message: ui`Text/Rich`({
        label: 'Message Content',
        default: 'This is an alert message.',
        visible: when`../display`.is.equal.to(true),
      }),
      dismissable: ui`Switch`({
        label: 'Dismissable',
        default: false,
        visible: when`../display`.is.equal.to(true),
      }),
    })({
      label: 'Alert Message',
    }),
    logo: ui`Form`.of({
      logoSize: ui`Choices`.of({
        xl: 'ExtraLarge',
        lg: 'Large',
        sm: 'Small',
      })({
        label: 'Logo Size',
        default: 'lg',
      }),
      logo: ui`Upload/Image`({ label: 'Logo' }),
      imgAlt: ui`Text`({ label: 'Alt Text' }),
    })({
      label: 'Logo',
      visible: when`../preset`.is.equal.to('custom').and
      .when`../type`.isnt.equal.to('tier_5').and
      .when`../type`.isnt.equal.to('tier_6')
    }),
    cutawayImage: ui`Form`.of({
      size: ui`Choices`.of({
        xl: 'ExtraLarge',
        lg: 'Large',
        sm: 'Small',
      })({
        label: 'Cut-away Image Size',
        default: 'lg',
      }),
      image: ui`Upload/Image`({ label: 'Cut-away Image' }),
      imgAlt: ui`Text`({ label: 'Alt Text' }),
    })({
      label: 'Cut-away Image',
      visible: when`../preset`.is.equal.to('custom').and
        .when`../type`.is.equal.to('tier_2'),
    }),
    ctas: ui`CTA/List`({
      max: when`../preset`.is.equal.to('custom').and.when`../type`.is.equal.to(
        'tier_3'
      ).and.when`../showHeroImage`.is.equal
        .to('small')
        .then(2)
        .otherwise(4),
      visible: when`../type`.isnt.equal.to('tier_6').and.when`../carousel/enabled`.isnt.equal.to(true),
    }),
    carouselItems: ui`List`.of({
      mediaType: ui`Choices`.of({
        image: 'Image',
        video: 'Video',
      })({
        label: 'Media Type',
        default: 'image',
      }),
      image: ui`Image`({
        label: 'Image',
        crop: true,
        visible: when`../mediaType`.is.equal.to('image'),
      }),
      mp4: ui`Text`({
        label: 'MP4',
        visible: when`../mediaType`.is.equal.to('video'),
      }),
      webm: ui`Text`({
        label: 'WEBM',
        visible: when`../mediaType`.is.equal.to('video'),
      }),
      heading: ui`Text/Rich/Header`({
        label: 'Heading',
        default: 'Heading',
      }),
      posthead: ui`Text`({
        label: 'Posthead',
        default: 'Posthead',
      }),
      ctas: ui`CTA/List`({
        label: 'CTAs',
      }),
    })({
      singular: 'Item',
      label: 'Carousel Items',
      visible: when`../carousel/enabled`.is.equal.to(true),
      max: 10,
    }),
  },
});
